import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../RestAPI";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService.js";
import "../SharedTable.css";
import { FiGitBranch, FiChevronDown, FiChevronRight, FiRefreshCw, FiGitMerge, FiGitCommit, FiFileText, FiClock, FiUser, FiCheck } from "react-icons/fi";
import { CircularProgress } from "@mui/material";
import FilterDropdown from "../Common Components/FilterDropdown";
import useCurrentProject, { resolveProject } from "../Common Components/useCurrentProject";

const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };
const fmtDateTime = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return d; } };

/* ── Timeline styles ── */
const tlDot = { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, marginTop: 3 };
const tlLine = { position: 'absolute', left: 4, top: 14, bottom: 0, width: 2, background: 'var(--border-secondary)' };
const tlItem = { display: 'flex', gap: 10, position: 'relative', paddingBottom: 12, paddingLeft: 0 };
const tlContent = { flex: 1, minWidth: 0 };
const tlLabel = { fontSize: '0.62rem', color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 1 };
const tlMain = { fontSize: '0.72rem', color: 'var(--text-primary)', lineHeight: 1.4 };
const tlSub = { fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 1 };
const tlTime = { fontSize: '0.62rem', color: 'var(--text-subtle)', flexShrink: 0, textAlign: 'right', minWidth: 110 };
const sectionTitle = { fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', margin: '10px 0 6px', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: 5 };

const BranchTimeline = ({ br, syncData }) => {
    const commits = syncData.commits || [];
    const mrs = syncData.merge_requests || [];
    const changedFiles = syncData.changed_files || [];
    const mainMR = mrs.length > 0 ? mrs[0] : null;

    // Build timeline events — each gets its actual GitLab timestamp
    const events = [];

    // 1. Branch created — use the earliest commit date as the real branch creation time
    const commitDates = commits.map(c => c.date).filter(Boolean).map(d => new Date(d).getTime());
    const earliestCommitDate = commitDates.length > 0 ? new Date(Math.min(...commitDates)).toISOString() : null;
    events.push({
        type: 'created',
        color: 'var(--status-green)',
        title: `Branch created: ${br.branch_name}`,
        detail: `Repository: ${br.repo_name} (${br.repo_type})${br.developer_name ? ` • Developer: ${br.developer_name}` : ''}`,
        time: earliestCommitDate || br.created_at_gitlab || br.created_time,
    });

    // 2. Each commit with its actual committed_date from GitLab
    commits.forEach(c => {
        events.push({
            type: 'commit',
            color: 'var(--accent)',
            title: c.message,
            detail: `${c.short_sha} by ${c.author}`,
            time: c.date,  // This is committed_date from GitLab — has full timestamp
        });
    });

    // 3. MR opened — use MR's created_at from GitLab
    if (mainMR && mainMR.created_at) {
        events.push({
            type: 'mr_opened',
            color: 'var(--status-blue)',
            title: `MR #${mainMR.iid} opened: ${mainMR.title}`,
            detail: `Target: ${mainMR.target_branch}${syncData.mr_reviewers?.length ? ` • Reviewers: ${syncData.mr_reviewers.join(', ')}` : ''}${syncData.mr_pipeline_status ? ` • Pipeline: ${syncData.mr_pipeline_status}` : ''}`,
            time: mainMR.created_at,  // GitLab MR created_at
        });
    }

    // 4. Merged — use merged_at from GitLab
    if (br.merge_status === 'merged' && br.merged_at) {
        events.push({
            type: 'merged',
            color: 'var(--status-green)',
            title: `Merged into ${br.target_branch}`,
            detail: `Merged by ${br.merged_by || 'unknown'}${br.files_changed > 0 ? ` • ${br.files_changed} files changed (+${br.additions}/-${br.deletions})` : ''}`,
            time: br.merged_at,  // GitLab merged_at
        });
    }

    // 5. MR closed (not merged)
    if (br.merge_status === 'closed') {
        events.push({
            type: 'closed',
            color: 'var(--status-red)',
            title: `MR closed without merge`,
            detail: `Target was: ${br.target_branch || '-'}`,
            time: br.last_synced_at,
        });
    }

    // Sort: newest first (reverse chronological)
    events.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

    return (
        <>
            {/* Summary stats bar */}
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 14, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
                <div><span style={tlLabel}>Status</span><div style={tlMain}><span className={`fd-status-pill ${br.merge_status === 'merged' ? 'fd-status-pill--success' : br.merge_status === 'open' ? 'fd-status-pill--info' : 'fd-status-pill--muted'}`}>{br.merge_status === 'merged' ? 'Merged' : br.merge_status === 'open' ? 'Open MR' : br.merge_status === 'closed' ? 'Closed' : 'No MR'}</span></div></div>
                <div><span style={tlLabel}>Commits</span><div style={tlMain}>{br.total_commits}</div></div>
                <div><span style={tlLabel}>Files</span><div style={tlMain}>{br.files_changed > 0 ? `${br.files_changed} (+${br.additions}/-${br.deletions})` : '-'}</div></div>
                {br.target_branch && <div><span style={tlLabel}>Target Branch</span><div style={{ ...tlMain, color: 'var(--status-link)' }}>{br.target_branch}</div></div>}
                {br.merge_request_iid && <div><span style={tlLabel}>MR</span><div style={tlMain}>#{br.merge_request_iid} — {br.mr_title || '-'}</div></div>}
                <div><span style={tlLabel}>Last Synced</span><div style={tlMain}>{fmtDateTime(br.last_synced_at)}</div></div>
            </div>

            {/* Timeline */}
            <h5 style={sectionTitle}><FiClock size={11} /> Branch Lifecycle Timeline ({events.length} events)</h5>
            <div style={{ position: 'relative', paddingLeft: 6, marginBottom: 14 }}>
                {events.map((evt, i) => (
                    <div key={i} style={tlItem}>
                        {i < events.length - 1 && <div style={tlLine} />}
                        <div style={{ ...tlDot, background: evt.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.5rem' }} />
                        <div style={tlContent}>
                            <div style={tlMain}>{evt.title}</div>
                            {evt.detail && <div style={tlSub}>{evt.detail}</div>}
                        </div>
                        <div style={tlTime}>{fmtDateTime(evt.time)}</div>
                    </div>
                ))}
            </div>

            {/* Changed files */}
            {changedFiles.length > 0 && (
                <div>
                    <h5 style={sectionTitle}><FiFileText size={11} /> Changed Files ({changedFiles.length})</h5>
                    <div style={{ maxHeight: 180, overflowY: 'auto', scrollbarWidth: 'thin', background: 'var(--bg-secondary)', borderRadius: 6, padding: '6px 10px', border: '1px solid var(--border-subtle)' }}>
                        {changedFiles.map((f, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, padding: '3px 0', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                                <span style={{ color: f.new_file ? 'var(--status-green)' : f.deleted ? 'var(--status-red)' : f.renamed ? 'var(--status-blue)' : 'var(--text-muted)', flexShrink: 0, width: 14, textAlign: 'center' }}>
                                    {f.new_file ? '+' : f.deleted ? '-' : f.renamed ? 'R' : 'M'}
                                </span>
                                <span style={{ color: 'var(--text-secondary)' }}>{f.new_path || f.old_path}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

const BranchAuditReport = () => {
    const [sprints, setSprints] = useState([]);
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useCurrentProject();
    const [currentSprint, setCurrentSprint] = useState('');
    const [reportData, setReportData] = useState([]);
    const [expandedTickets, setExpandedTickets] = useState({});
    const [expandedBranches, setExpandedBranches] = useState({});
    const [syncing, setSyncing] = useState(false);
    const [loading, setLoading] = useState(false);

    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };

    const getProjects = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}core/project/`, { headers });
            const data = await res.json();
            if (data.status === "success") { setProjects(data.data); setCurrentProject(prev => resolveProject(data.data, prev)); }
        } catch (e) { console.error(e); }
    }, []);

    const getSprints = useCallback(async (projectId) => {
        try {
            const res = await fetch(`${API_BASE_URL}ticketing/sprint/?project_id=${projectId}`, { headers });
            const data = await res.json();
            if (data.status === "success") {
                const sorted = (data.data || []).sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
                setSprints(sorted);
                if (sorted.length) setCurrentSprint(sorted[0].id);
            }
        } catch (e) { console.error(e); }
    }, []);

    const getReportData = useCallback(async (sprintId) => {
        if (!sprintId) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}ticketing/gitlab/sprint-report/?sprint_id=${sprintId}`, { headers });
            const data = await res.json();
            if (data.status === "success") setReportData(data.data || []);
            else showErrorToast(data.message);
        } catch (e) { console.error(e); showErrorToast("Failed to load report"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { getProjects(); }, []);
    useEffect(() => { if (currentProject) getSprints(currentProject); }, [currentProject]);
    useEffect(() => { if (currentSprint) { getReportData(currentSprint); setExpandedTickets({}); setExpandedBranches({}); } else setReportData([]); }, [currentSprint]);

    const handleSyncAll = async () => {
        if (!currentSprint) return;
        setSyncing(true);
        try {
            const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/sync-all/`, {
                method: "POST", headers,
                body: JSON.stringify({ sprint_id: currentSprint }),
            });
            const data = await res.json();
            if (data.status === "success") {
                showSuccessToast(data.message);
                getReportData(currentSprint);
            } else showErrorToast(data.message);
        } catch (e) { showErrorToast("Sync failed"); }
        finally { setSyncing(false); }
    };

    const toggleTicket = (id) => setExpandedTickets(p => ({ ...p, [id]: !p[id] }));
    const toggleBranch = (id) => setExpandedBranches(p => ({ ...p, [id]: !p[id] }));

    const mergePillClass = (s) => s === 'merged' ? 'fd-status-pill--success' : s === 'open' ? 'fd-status-pill--info' : s === 'closed' ? 'fd-status-pill--danger' : 'fd-status-pill--muted';
    const mergeLabel = (s) => s === 'merged' ? 'Merged' : s === 'open' ? 'Open MR' : s === 'closed' ? 'Closed' : 'No MR';

    const totalBranches = reportData.reduce((sum, t) => sum + (t.branches?.length || 0), 0);

    return (
        <div style={{ padding: '20px 28px' }}>
            <div className="fd-selector-row">
                <FilterDropdown
                    label="Project"
                    value={currentProject}
                    options={projects}
                    onChange={setCurrentProject}
                    renderLabel={o => o?.project_name || ''}
                    getValue={o => String(o?.id)}
                />
                <FilterDropdown
                    label="Sprint"
                    value={currentSprint}
                    options={sprints}
                    onChange={setCurrentSprint}
                    renderLabel={o => o?.sprint_name || ''}
                    getValue={o => String(o?.id)}
                />
            </div>

            <div className="fd-card">
                <div className="fd-card-header">
                    <div className="fd-card-header-left">
                        <h1 className="fd-card-title">Branch Audit Report</h1>
                        <p className="fd-card-subtitle">Sprint → Tickets → Branches with full lifecycle, commits, and merge details</p>
                    </div>
                    <div className="fd-card-actions">
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-subtle)' }}>{reportData.length} tickets • {totalBranches} branches</span>
                        <button className="fd-btn-primary" onClick={handleSyncAll} disabled={syncing || !currentSprint}>
                            {syncing ? <><CircularProgress size={12} sx={{ color: '#fff' }} /> Syncing...</> : <><FiRefreshCw size={12} /> Sync All</>}
                        </button>
                    </div>
                </div>

                <div style={{ padding: '12px 20px 16px' }}>
                    {loading ? (
                        <div className="fd-empty"><CircularProgress size={28} sx={{ color: 'var(--accent)' }} /></div>
                    ) : reportData.length > 0 ? reportData.map(ticket => {
                        const isOpen = expandedTickets[ticket.ticket_id];
                        const branchCount = ticket.branches?.length || 0;
                        const devNames = Array.isArray(ticket.developers) ? ticket.developers.map(d => d.name || d).join(', ') : '-';

                        return (
                            <div key={ticket.ticket_id} style={{ marginBottom: 8, border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
                                {/* Ticket header */}
                                <div onClick={() => toggleTicket(ticket.ticket_id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: 'var(--bg-tertiary)', transition: 'background 0.15s' }}>
                                    {isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)' }}>{ticket.ticket_code}</span>
                                    <span className={`fd-status-pill ${ticket.ticket_type === 'Bug' ? 'fd-status-pill--danger' : 'fd-status-pill--info'}`}>{ticket.ticket_type}</span>
                                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.description}</span>
                                    <span style={{ fontSize: '0.62rem', color: 'var(--text-subtle)', flexShrink: 0 }}>
                                        {devNames} • <FiGitBranch size={10} /> {branchCount}
                                    </span>
                                </div>

                                {/* Expanded: branches */}
                                {isOpen && (
                                    <div style={{ padding: '8px 14px 12px' }}>
                                        {branchCount > 0 ? ticket.branches.map(br => {
                                            const brOpen = expandedBranches[br.id];
                                            const syncData = br.last_sync_data || {};
                                            const commits = syncData.commits || [];
                                            const mrs = syncData.merge_requests || [];
                                            const changedFiles = syncData.changed_files || [];
                                            const hasSyncData = br.last_synced_at && commits.length > 0;

                                            return (
                                                <div key={br.id} style={{ marginBottom: 6, border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
                                                    {/* Branch summary row */}
                                                    <div onClick={() => toggleBranch(br.id)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', background: 'var(--bg-secondary)', transition: 'background 0.15s' }}>
                                                        {brOpen ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                                                        <FiGitBranch size={12} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                                                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-primary)' }}>{br.branch_name}</span>
                                                        <span style={{ fontSize: '0.62rem', color: 'var(--text-subtle)' }}>{br.repo_name} ({br.repo_type})</span>
                                                        <span className={`fd-status-pill ${mergePillClass(br.merge_status)}`}>{mergeLabel(br.merge_status)}</span>
                                                        {br.developer_name && <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}><FiUser size={9} /> {br.developer_name}</span>}
                                                        <span style={{ marginLeft: 'auto', fontSize: '0.6rem', color: 'var(--text-subtle)' }}>
                                                            {br.total_commits > 0 ? `${br.total_commits} commits` : 'Not synced'}
                                                        </span>
                                                    </div>

                                                    {/* Branch full audit log */}
                                                    {brOpen && (
                                                        <div style={{ padding: '14px 16px', background: 'var(--bg-tertiary)', borderTop: '1px solid var(--border-subtle)' }}>
                                                            {!br.last_synced_at ? (
                                                                <p style={{ fontSize: '0.72rem', color: 'var(--text-subtle)', textAlign: 'center', padding: '12px 0' }}>
                                                                    No sync data available. Click "Sync All" to fetch details from GitLab.
                                                                </p>
                                                            ) : (
                                                                <BranchTimeline br={br} syncData={syncData} />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }) : (
                                            <p style={{ fontSize: '0.72rem', color: 'var(--text-subtle)', padding: '4px 0' }}>No branches tracked for this ticket</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    }) : (
                        <div className="fd-empty" style={{ padding: '40px 20px' }}>No tickets found for this sprint</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BranchAuditReport;
